import type { RaceSize } from '../../gen/modes';

/**
 * A live race's rules (docs/LIVE.md): who won, who took each coin, and what each racer is paid.
 * Pure, so the room and both phones reach the same answer.
 */

/** Seats in a room: the inviter is 0; a race has two, a party (docs/PARTY.md) up to four. */
export type Seat = 0 | 1 | 2 | 3;

/** The winner's bonus by size (a draw pays half to each; losing pays none). */
export const RACE_WIN_BONUS: Record<RaceSize, number> = { quick: 10, normal: 20, long: 30 };

/** Races a day that pay (bonus and coins), so trading wins cannot farm coins. */
export const RACE_PAID_PER_DAY = 5;

/** Seconds a racer may be away (the app hidden, the connection gone) before they forfeit. */
export const RACE_GRACE_SECONDS = 10;

/**
 * How a racer finished: a checked run (cleared or not, with how much grass it left, and whether
 * the mower broke down or ran dry), or out (forfeited, or a run that did not check out).
 */
export type RaceEntry =
  | { kind: 'run'; cleared: boolean; ticks: number; grassLeft: number; crashed?: boolean }
  | { kind: 'out' };

/**
 * The winner, or null for a draw. A run beats an out; a cleared lawn beats an uncleared one; two
 * cleared lawns go to the fewer ticks. A mower that broke down loses to one still going (owner,
 * 2026-10-25: the other just wins, without mowing the rest; their phone stops the race when it
 * sees the crash); two that broke down go to the later crash. Otherwise two uncleared lawns go to
 * the less grass left. Ties are draws.
 */
export function raceWinner(a: RaceEntry, b: RaceEntry): Seat | null {
  if (a.kind === 'out' || b.kind === 'out') {
    if (a.kind === b.kind) return null;
    return a.kind === 'out' ? 1 : 0;
  }
  if (a.cleared !== b.cleared) return a.cleared ? 0 : 1;
  if (a.cleared) return a.ticks === b.ticks ? null : a.ticks < b.ticks ? 0 : 1;
  const [ca, cb] = [a.crashed === true, b.crashed === true];
  if (ca !== cb) return ca ? 1 : 0;
  if (ca && a.ticks !== b.ticks) return a.ticks > b.ticks ? 0 : 1;
  return a.grassLeft === b.grassLeft ? null : a.grassLeft < b.grassLeft ? 0 : 1;
}

/**
 * Who took each coin: the racer whose mower picked it up at the earlier tick (-1: never). The same
 * tick, or neither, and nobody has it.
 */
export function coinTakers(a: readonly number[], b: readonly number[]): (Seat | null)[] {
  return a.map((ta, i) => {
    const tb = b[i] ?? -1;
    if (ta < 0 && tb < 0) return null;
    if (tb < 0) return 0;
    if (ta < 0) return 1;
    return ta === tb ? null : ta < tb ? 0 : 1;
  });
}

export interface RacePay {
  bonus: number;
  coins: number;
}

/**
 * What a seat is paid: the bonus for a win (half for a draw), and a coin for each coin it took.
 * Nothing once the day's paid races are used up (`paidToday` races already paid).
 */
export function racePay(
  size: RaceSize,
  seat: Seat,
  winner: Seat | null,
  takers: readonly (Seat | null)[],
  paidToday: number,
): RacePay {
  if (paidToday >= RACE_PAID_PER_DAY) return { bonus: 0, coins: 0 };
  const full = RACE_WIN_BONUS[size];
  const bonus = winner === seat ? full : winner === null ? Math.floor(full / 2) : 0;
  return { bonus, coins: takers.filter((t) => t === seat).length };
}

/**
 * Head to head (docs/HEAD_TO_HEAD.md): what a seat is paid, the race's bonus for a win (half for a
 * draw) and the coins it picked up; nothing once the day's paid matches are used up (races and
 * head to head together). A party of three or four (`places`, docs/PARTY.md) pays by place
 * instead: 1st the full bonus, 2nd half, 3rd and 4th a quarter.
 */
export function matchPay(
  size: RaceSize,
  seat: Seat,
  winner: Seat | null,
  coins: number,
  paidToday: number,
  places?: readonly number[],
): RacePay {
  if (paidToday >= RACE_PAID_PER_DAY) return { bonus: 0, coins: 0 };
  const full = RACE_WIN_BONUS[size];
  if (places && places.length > 2) {
    const place = places[seat] ?? places.length;
    const bonus = place === 1 ? full : place === 2 ? Math.floor(full / 2) : Math.floor(full / 4);
    return { bonus, coins };
  }
  const bonus = winner === seat ? full : winner === null ? Math.floor(full / 2) : 0;
  return { bonus, coins };
}

/**
 * Places from scores (1 first; the same score shares the better place): `better` says which way
 * scores go. `tiers` (by seat, 0 when absent) comes first: a higher tier places after every lower
 * one (a mower that broke down after those still going, one that left after both), and within a
 * tier the scores decide.
 */
export function placesFrom(
  scores: readonly number[],
  better: 'higher' | 'lower',
  tiers: readonly number[] = [],
): number[] {
  const key = (i: number) => [tiers[i] ?? 0, better === 'higher' ? -scores[i]! : scores[i]!];
  return scores.map((_, i) => {
    const [ti, si] = key(i);
    return (
      1 +
      scores.filter((_, j) => {
        const [tj, sj] = key(j);
        return tj! < ti! || (tj === ti && sj! < si!);
      }).length
    );
  });
}
